import React, { Component, createRef } from 'react';
import cx from 'classnames';
import PropTypes from 'prop-types';
import { noop } from 'lodash';
import { NewButton as Button } from 'components/Button';
import { renderProp, setRefs, cloneElement, isElementOfComponent, omitProps } from 'utils/react';
import { eventHasKey, KEY } from 'utils/dom';
import { isForwardRefComponent } from 'utilsTS/react';
import {
    defaultProps as toggleableDefaultProps,
    propTypes as toggleablePropTypes,
    ToggleableContext,
} from 'containers/Toggleable';
import styles from './DropdownToggleWrapper.module.scss';

export default class DropdownToggleWrapper extends Component {
    static displayName = 'DropdownToggleWrapper';

    static propTypes = {
        ...toggleablePropTypes,
        id: PropTypes.string,
        tabIndex: PropTypes.number,
        disabled: PropTypes.bool,
        renderToggle: PropTypes.func,
        toggle: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
        useNewAria: PropTypes.bool,
        openOnFocus: PropTypes.bool,
        openOnKeyDown: PropTypes.bool,
        toggleRef: PropTypes.ref(),
        toggleOnClick: PropTypes.bool,
        toggleWithSpace: PropTypes.bool,
        toggleWithEnter: PropTypes.bool,
        closeWithTab: PropTypes.bool,
        onKeyDown: PropTypes.func,
        onClick: PropTypes.func,
        onFocus: PropTypes.func,
        className: PropTypes.string,
    };

    static contextType = ToggleableContext;

    static defaultProps = {
        ...toggleableDefaultProps,
        id: '',
        tabIndex: 0,
        disabled: false,
        useNewAria: false,
        openOnFocus: false,
        openOnKeyDown: false,
        toggleRef: noop,
        toggleOnClick: true,
        toggleWithSpace: true,
        toggleWithEnter: true,
        closeWithTab: true,
        onKeyDown: noop,
        onClick: noop,
        onFocus: noop,
        className: null,
    };

    ref = createRef();

    focusToggle = () => {
        this.ref.current?.focus();
    };

    handleToggleClick = (event) => {
        const { disabled, toggleOnClick, onClick } = this.props;
        const { toggle } = this.context;

        onClick(event);

        if (disabled || !toggleOnClick) return;

        toggle();
        this.focusToggle();
    };

    handleToggleFocus = (event) => {
        const { disabled, openOnFocus, onFocus } = this.props;
        const { isOpen, open } = this.context;

        onFocus(event);

        if (disabled || isOpen || !openOnFocus) return;
        open();
    };

    handleToggleKeyDown = (event) => {
        const { onKeyDown } = this.props;

        event.stopPropagation();

        const { TAB, ENTER, SPACE } = KEY;

        if (eventHasKey(event, ENTER, SPACE)) {
            this.handleKeyDownEnterSpace(event);
        } else if (eventHasKey(event, TAB)) {
            this.handleKeyDownTab(event);
        }

        this.handleKeyDownRest(event);
        onKeyDown(event);
    };

    handleKeyDownEnterSpace = (event) => {
        const { ENTER, SPACE } = KEY;
        const { toggleWithEnter, toggleWithSpace } = this.props;
        const { isOpen, toggle } = this.context;

        const isEnter = eventHasKey(event, ENTER) && toggleWithEnter;
        const isSpace = eventHasKey(event, SPACE) && toggleWithSpace;

        if (isEnter || isSpace) {
            event.preventDefault();

            if (isOpen) {
                this.focusToggle();
            }
            toggle();
        }
    };

    handleKeyDownTab = () => {
        const { closeWithTab } = this.props;
        const { isOpen, close } = this.context;

        if (isOpen && closeWithTab) {
            close();
        }
    };

    handleKeyDownRest = (event) => {
        const { TAB } = KEY;
        const { openOnKeyDown } = this.props;
        const { isOpen, open } = this.context;

        if (eventHasKey(event, TAB)) return;

        if (!isOpen && openOnKeyDown) {
            open();
        }
    };

    render() {
        const {
            id,
            tabIndex,
            disabled,
            renderToggle,
            toggle,
            toggleRef,
            className,
            useNewAria,
            ...rest
        } = this.props;
        const { isOpen } = this.context;

        const innerRef = setRefs(this.ref, toggleRef);

        const props = {
            id,
            tabIndex,
            disabled,
            ...omitProps(rest, DropdownToggleWrapper),
            'aria-controls': isOpen || useNewAria ? `${id}-popover` : null,
            'aria-haspopup': useNewAria ? true : null,
            'aria-expanded': isOpen,
            onKeyDown: this.handleToggleKeyDown,
            onClick: this.handleToggleClick,
            onFocus: this.handleToggleFocus,
        };

        const renderedToggle = renderProp(toggle, renderToggle, { isOpen });
        const classNames = cx(styles.toggle, className);

        if (React.isValidElement(renderedToggle)) {
            const isDOMElement = typeof renderedToggle.type === 'string';
            const hasInnerRef = Object.keys(renderedToggle.props).includes('innerRef');
            const isKnownElement = isElementOfComponent(renderedToggle, [
                Button,
                'DropdownToggle',
                'SelectToggle',
                'FilteredSelectInput',
                'PrimaryNavigationButton',
                'TextareaInput',
            ]);

            if (isDOMElement || hasInnerRef || isKnownElement) {
                const refProp = isForwardRefComponent(renderedToggle) ? 'ref' : 'innerRef';

                return cloneElement(renderedToggle, props, {
                    [refProp]: innerRef,
                    className: classNames,
                });
            }

            return (
                <span ref={innerRef} className={classNames}>
                    {cloneElement(renderedToggle, props)}
                </span>
            );
        }

        return null;
    }
}
